/**
 * Resultado explicito de una operacion que puede fallar, sin recurrir a excepciones.
 * Se usa en las fronteras entre modulos para que el llamador este obligado a
 * tratar el caso de error.
 *
 * @example
 * const parsed = parsePort('3001');
 * if (!parsed.ok) return parsed;      // propaga el error
 * listen(parsed.value);
 */
export type Result<T, E> = Ok<T> | Err<E>;

export interface Ok<T> {
  readonly ok: true;
  readonly value: T;
}

export interface Err<E> {
  readonly ok: false;
  readonly error: E;
}

export const ok = <T>(value: T): Ok<T> => ({ ok: true, value });

export const err = <E>(error: E): Err<E> => ({ ok: false, error });

export const isOk = <T, E>(result: Result<T, E>): result is Ok<T> => result.ok;

export const isErr = <T, E>(result: Result<T, E>): result is Err<E> => !result.ok;

/** Transforma el valor de un resultado exitoso; los errores se propagan sin cambios. */
export const mapResult = <T, U, E>(result: Result<T, E>, fn: (value: T) => U): Result<U, E> =>
  result.ok ? ok(fn(result.value)) : result;

/**
 * Extrae el valor o lanza el error. Reservado para puntos de arranque (bootstrap)
 * donde un fallo debe detener el proceso.
 */
export const unwrap = <T, E>(result: Result<T, E>): T => {
  if (result.ok) return result.value;
  throw result.error instanceof Error ? result.error : new Error(String(result.error));
};
